import { cn } from '@/lib/utils';

/**
 * The CoreWay mark: a road seen in perspective, converging on a vanishing point up and to the right.
 *
 * Traced from docs/assets/mockup-dispatchos-v2.png at 12x magnification (crop 28,46 - 96,112) by
 * scanning the PNG for the blue and white runs row by row. There is NO square container: it is a
 * free-standing mark that widens downward and is cut flat on a horizontal baseline.
 *
 * The three elements, in the order they were measured:
 *   1. the left edge stroke, a curve that tapers upward (the left shoulder);
 *   2. four white lane dashes in perspective, the bottom one largest and widest;
 *   3. the right edge stroke, slightly taller, that nearly meets the left one at the vanishing point.
 *
 * The viewBox is the measured bounding box: 44 x 44 device pixels in the mockup, origin at (31,53).
 * Every coordinate below is a real sample, not an eyeballed curve.
 *
 * COLOUR IS NOT BAKED IN. The two strokes paint with `currentColor` so the call site sets them from a
 * token, and the lane dashes take a fill utility. Nothing here is a hex — see public/favicon.svg for
 * the one file that cannot do this.
 */
export function RoadMark({
  className,
  laneClassName = 'fill-on-rail',
  title,
}: {
  className?: string;
  /** Fill utility for the lane dashes. The asphalt is the surface behind the mark. */
  laneClassName?: string;
  /** Give it a title only when the mark stands alone; beside the wordmark it is decorative. */
  title?: string;
}) {
  return (
    <svg
      viewBox="0 0 44 44"
      className={cn('shrink-0', className)}
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}

      {/* Left shoulder: outer edge sweeping from the baseline to the vanishing point, inner edge back
          down. The band is 9 wide at the baseline and closes to nothing at the tip. */}
      <path d="M0 43 C1 29 22 5 32 0.5 C28 6 12 22 9 43 Z" fill="currentColor" />

      {/* Right shoulder: nearly upright at the baseline, bending right as it climbs, and ending half a
          unit above the left tip — the two never quite touch. */}
      <path d="M39 43 C37.5 32 38 12 43 0 C38 5 27 18 26 43 Z" fill="currentColor" />

      {/* The centre line, four dashes, bottom to top. Each is a trapezoid: the far edge is both higher
          and narrower, which is what makes the road read as receding. */}
      <g className={laneClassName}>
        <path d="M17 31 L23.5 31 L21.5 43 L14 43 Z" />
        <path d="M22 21 L25.5 21 L24.5 26.8 L18.8 26.8 Z" />
        <path d="M26.5 14 L29.5 14 L26 19.8 L22.8 19.8 Z" />
        <path d="M35 2 L36.2 2 L29.5 11.5 L27.6 11.5 Z" />
      </g>
    </svg>
  );
}
